
/* ⭐️ Combobox Patterns ⭐️

    ☑️ slds-has-focus should be on <input /> or .slds-combobox__input 
        - Should stay like that the entire time until blur/focusout

    ☑️ Open Combobox (with click/'Enter'/spacebar(' ') )
        1. add .slds-is-open to .slds-dropdown-trigger
        2. add aria-expaned="true" to <input />  (if no input add to .slds-combobox__input) 
        3. Remove .slds-has-focus & aria-selected="true" from all .slds-listbox__option with role="option"
        4. add .slds-has-focus & aria-selected="true" to the first option 
            - if an option is selected add .slds-has-focus & aria-selected="true"

    ☑️ Open Combobox ('ArrowUp', 'ArrowDown', Type-Ahead)
        👉 Execute Steps 1-3 as described in "Open Combobox (with click/enter/spacebar)"
        1. ArrowDown 
            -  add .slds-has-focus & aria-selected="true" to the first option
        2. ArrowUp 
            - add .slds-has-focus & aria-selected="true" to the first option
        3. Type-ahead (No search)
            - Call handleReadOnlySearch, imported from ../../../utils/comboboxUtils.js
        👉 scroll into view on the focused option
    
    ☑️ Close Combobox (triggers: 'Escape'/'Tab', option selection, focusout/blur)
        1. remove .slds-is-open from .slds-dropdown-trigger
        2. set aria-expaned="false" on <input /> (or .slds-combobox__input if no <input />) 

    ☑️ Navigating Options (.slds-listbox__option[role="option"])
        👉 remove .slds-has-focus & aria-selected="true" from all options
        👉 add .slds-has-focus & aria-selected="true"
        1. ArrowDown 
            - Navigate to _options[focusedIndex + 1]
            - if at last option, go to the first option 
        2. ArrowUp
            - Navigate to _options[focusedIndex + 1]
            - if at first option, go to the last option 
        3. Escape 
            - close combobox 
        4. Tab 
            - close combobox 
        5. Enter 
            - select option / close combobox 
        6. Type-Ahead (alphaNumeric) 
            - Add slds-has-focus / aria-selected="true" to the top match

    ☑️ Select an Option (.slds-listbox__option[role="option"])
        👉 Close the combobox once an option is selected (if not multi-select)
        1. Click 
            - Remove .slds-has-focus/.slds-is-selected/aria-selected="true"/aria-checked="true" 
              from all .slds-listbox__option[role="option"]
            - Add .slds-has-focus/.slds-is-selected/aria-selected="true"/aria-checked="true" 
              from all .slds-listbox__option[role="option"]
        2. Enter 
            - Add .slds-is-selected/aria-checked="true" to the option that currently has 
              .slds-has-focus/aria-selected="true"
        

*/

/* ⭐️ Combobox Event Listeners ⭐️
    ☑️ <input /> 
        1. onkeydown={handleInputKeyDown}
            - event.key = alphaNumeric
                > If readonly --> Handle type-ahead  
                > If search --> Filter the options 
            - ⛔️ Ignore The following: 
                > ⛔️ 'Enter', 'Escape', 'Shift', 'ArrowUp', 'ArrowDown', 
                  'ArrowLeft', 'ArrowRight'
        
        2. ❓ onchange={handleValueChange}
            - ❓ Not sure, see if this triggers.  
                > ❓ Can do dispatch event here maybe
    
    ☑️ <div class="slds-dropdown-trigger slds-dropdown-trigger_click"></div>
        1. onclick={toggleCombobox}  
            - Open/close the combobox 
            - Navigate to first or selected option

        2. onkeydown={toggleCombobox}
            - Tab/Escape
                > Close combobox 
            - Enter: 
                > If combobox is open --> Handle selecting the option with .slds-has-focus 
                > If combobox is closed --> Open combobox and nav to First/Selected option 
            - Alpha-Numeric
                > ⛔️ Ignore
            - ArrowUp/ArrowDown
                > Navigate options w/ .slds-has-focus/aria-selected 
    
    ☑️ <div class=".slds-listbox__option" role="option"></div>
        1. onclick={handleOptionClick}
            > Handle selecting the option & close the combobox (if not multiselect)

        2. onmousedown={deflectFocus}
            - Do not allow focus to leave input (for slds-input_faux .slds-combobox__input)  
                > event.preventDefault();
*/

// aria-selected="true" and slds-has-focus goes on focused .slds-listbox__option item 
// aria-checked="true" and .slds-is-selected always on the selected .slds-listbox__option item
// .slds-is-open applied to .slds-dropdown-trigger 
/* when combobox is open add aria-expaned="true" to <input /> 
    ✚ .slds-is-open to .slds-dropdown-trigger */

export function handleUnifiedClick(cmp, event, onlyFocus=false) {
    const trgt = event.target;
    console.log('here is event.target in handleUnifiedClick() of comboboxUtils.js: ', event.target);
    if (
        !trgt.classList.contains('gw-unified-form-element') && 
        !trgt.classList.contains('slds-form-element__label') && 
        !trgt.classList.contains('slds-form-element') &&
        !trgt.classList.contains('slds-button_icon')
    ) {
        return;
    }
    
    if (cmp._unifiedCl && !cmp._unifiedCl.includes('slds-is-disabled')) {
        const trigger = cmp.template.querySelector('.slds-dropdown-trigger');
        const inp = cmp.template.querySelector('.slds-input');
        if (trigger) {
            inp.focus();
            if (!onlyFocus) {
                trigger.click();
            }
        }
    }
}

export function handleDropdownFlip(cmp) {
    if (cmp.suppressFlip) {return;}
    const dropdown = cmp.template.querySelector('.slds-dropdown_fluid');
    if (!dropdown) return;

    if (cmp.alwaysFlip) {
        if (!dropdown.classList.contains('dropdown-up')) {
            dropdown.classList.add('dropdown-up');
        }
        return;
    }
    // Reset any previous flip
    dropdown.classList.remove('dropdown-up');

    const rect = dropdown.getBoundingClientRect();
    const overflowBottom = rect.bottom > window.innerHeight;
    const overflowTop = rect.top < 0;

    // If it overflows below the viewport and has more space above, flip it
    if (overflowBottom && rect.top > rect.height) {
        dropdown.classList.add('dropdown-up');
    } else if (overflowTop) {
        // In case it somehow renders above the viewport already
        dropdown.classList.remove('dropdown-up');
    }
}

export function suppressEscape(event) {
    if (event?.key === 'Escape') {
        event.preventDefault();
    }
}


export function handleReadOnlySearch(event, currentStr, options) {
    const k = event?.key || '';
    const printable = k.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (k === 'Backspace') {
        currentStr = currentStr.slice(0, -1);
    } else if (printable) {
        currentStr += k;
    } else {
        return { nextStr: currentStr, match: null, index: -1 };
    }

    const q = String(currentStr.trim().toLowerCase());

    if (!q) {
        return { nextStr: currentStr, match: null, index: -1 };
    }

    const normalize = v => {
        if (v == null) return '';
        if (v instanceof Date) return v.toISOString();
        return String(v);
    };

    // 1) label.startsWith
    for (let i = 0; i < options.length; i++) {
        if (normalize(options[i].label).toLowerCase().startsWith(q)) {
        return { nextStr: currentStr, match: options[i], index: i };
        }
    }
    // 2) label.includes
    for (let i = 0; i < options.length; i++) {
        if (normalize(options[i].label).toLowerCase().includes(q)) {
        return { nextStr: currentStr, match: options[i], index: i };
        }
    }
    // 3) value.startsWith
    for (let i = 0; i < options.length; i++) {
        if (normalize(options[i].value).toLowerCase().startsWith(q)) {
        return { nextStr: currentStr, match: options[i], index: i };
        }
    }
    // 4) value.includes
    for (let i = 0; i < options.length; i++) {
        if (normalize(options[i].value).toLowerCase().includes(q)) {
        return { nextStr: currentStr, match: options[i], index: i };
        }
    }

    return { nextStr: currentStr, match: null, index: -1 };
}

export function handleDropdownLengthCl(options, customVal=null, dropdownCl=null) {
    const dropLens = [5, 7, 10];
    let baseCl = 'slds-dropdown slds-dropdown_fluid';
    if (dropdownCl) {
        baseCl = dropdownCl.split(' ')?.filter(s => 
            s && !s.startsWith('slds-dropdown_length-')
        ).join(' ');
    }

    if (customVal) {
        const numVal = Number(customVal);
        if (!isNaN(numVal)) {
            const match = dropLens.find(n => n === numVal);
            if (match) {
                return `${baseCl} slds-dropdown_length-${numVal}`;
            }
        }
    }
    if (options?.length) {
        const len = options.length; 
        return len > 14 ? `${baseCl} slds-dropdown_length-10`
            : len > 9 ? `${baseCl} slds-dropdown_length-7`
            : `${baseCl} slds-dropdown_length-5`;
    }

    return `${baseCl} slds-dropdown_length-5`;
}

export function handleReadOnlySearchComplex(event, currentStr, options) {
    // 1) Build the search string from this keystroke
    const k = event?.key || '';
    const isPrintable = k.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (k === 'Backspace') {
        currentStr = currentStr.slice(0, -1);
    } else if (isPrintable) {
        currentStr += k;
    } else {
        // ignore arrows, Enter, etc.
        return { nextStr: currentStr, match: null, index: -1 };
    }

    const q = currentStr.trim().toLowerCase();
    if (!q) return { nextStr: currentStr, match: null, index: -1 };

    // 2) Normalize options to a flat list
    const flat = Array.isArray(options)
        ? options.flatMap(o => Array.isArray(o?.options) ? o.options : [o])
        : [];

    // 3) Score candidates: exact > startsWith(label) > startsWith(value) > includes(label) > includes(value)
    const score = (opt) => {
        const label = String(opt.label ?? '').toLowerCase();
        const value = String(opt.value ?? '').toLowerCase();

        if (label === q || value === q) return 1000;

        if (label.startsWith(q)) return 900 - (label.length - q.length);
        if (value.startsWith(q)) return 800 - (value.length - q.length);

        const li = label.indexOf(q);
        if (li >= 0) return 700 - li;

        const vi = value.indexOf(q);
        if (vi >= 0) return 600 - vi;

        return -1e9; // no match
    };

    let bestIdx = -1, bestScore = -1e9;
    for (let i = 0; i < flat.length; i++) {
        const s = score(flat[i]);
        if (s > bestScore) {
        bestScore = s;
        bestIdx = i;
        }
    }

    const match = bestIdx >= 0 ? flat[bestIdx] : null;
    
    return { 
        nextStr: currentStr, 
        match, 
        index: bestIdx 
    };
}
// Handle List Option ON MOUSEDOWN   --->   for MENU (not combobox)
export function handleMenuItemMouseDown(event) {
    event.preventDefault();
    event.stopPropagation();

    const closestLi = event.target.closest('.slds-dropdown__item');

    if (closestLi) {
        const dropdownTrigger = closestLi.closest('.slds-dropdown-trigger');
        if (dropdownTrigger) {
            closestLi.classList.remove('dropdown-item-hover');
            dropdownTrigger.classList.remove('slds-is-open');
            return closestLi;
        } 
    }

}

// Handle 'slds-dropdown-trigger' keydowns 
export function navigateDropdownOptions(event) {
    // Let handleDropdownButtonClick handle 'Enter' Clicks
    if (event.key === 'Enter')  return;
    // Current Target should be 'slds-dropdown-trigger'
    const submenuTrigger = event.currentTarget;

    // Check if dropdown is open 
    const isOpen = submenuTrigger.classList.contains('slds-is-open');

    // Query the dropdown's list of options (<li> elements)
    const listOptions = Array.from(submenuTrigger.querySelectorAll('.slds-dropdown__item'));

    if (!isOpen && ['ArrowDown', 'ArrowUp'].some(keyType =>  keyType === event.key)) {
        submenuTrigger.classList.add('slds-is-open');
        return;
    }

    if (isOpen && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        const isDown = event.key === 'ArrowDown';
        const arrLength = listOptions.length;

        const currentSelected = listOptions.findIndex( (item) => item.classList.contains('dropdown-item-hover') );

        let nextIdx = 0 
        
        if (isDown) {
            nextIdx = currentSelected===-1 || currentSelected + 1 > arrLength -1
                ? 0
                : currentSelected + 1   
        } else {
            nextIdx = currentSelected===-1 || currentSelected - 1 < 0
                ? arrLength - 1
                : currentSelected - 1
        }

        // item with dropdown-item-hover
        const selected = listOptions[nextIdx];

        listOptions.forEach(opt => 
            opt === selected 
                ? opt.classList.add('dropdown-item-hover') 
                : opt.classList.remove('dropdown-item-hover')
            );
    } else if (isOpen && event.key === 'Escape') {
        listOptions.forEach(opt => opt.classList.remove('dropdown-item-hover'));
        submenuTrigger.classList.remove('slds-is-open');
    }
}

// Handle 'slds-button' Click/Enter  ---> i.e. the button for the dropdown trigger 
export function handleDropdownButtonClick(event) {
    try {
        // Get the dropdown button 
        const dropdownButton = event.target;

        // Check if aria-expanded is an attribute
        const isAriaExpanded = dropdownButton.getAttribute('aria-expanded') !== null; 
        
        // Query the dropdown trigger  
        const dropdownTrigger = dropdownButton.closest('.slds-dropdown-trigger');

        // Check if dropdown is open 
        const isOpen = dropdownTrigger.classList.contains('slds-is-open');

        // Set the val of aria-expanded attribute
        if (isAriaExpanded) dropdownButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        // Get the selected item if exists
        let hovered = null;

        if (isOpen) {
            // Close the dropdown
            if (!event.pointerType) {
                // Handle 'Enter' clicks on the button
                event.stopPropagation();
                // Query the hovered list option
                hovered = dropdownTrigger.querySelector('.dropdown-item-hover');
                // Remove the  hover
                hovered?.classList?.remove('dropdown-item-hover');
            }
            // Close the dropdown
            dropdownTrigger.classList.remove('slds-is-open');
        } else {
            // Open the dropdown
            dropdownTrigger.classList.add('slds-is-open');  
        }

        // Return the hovered item to update component's value  --->  or null
        return hovered;

    } catch(error) {
        console.error('Error in handleDropdownButtonClick() of comboboxUtils.js: ', error);
    }
}


// ________________________________________________________________________________________________ \\ 
// ________________________________________ Combobox Utils ________________________________________ \\

// ---------------- Combobox Keyboard Navigation ---------------- \\
// Get the hovered item if enter is clicked
export function getSelectedOptionOnEnterUtil(dropdownOptions) {
    try {
        const focusedIndex = dropdownOptions.findIndex(option => option.classList.contains('dropdown-item-hover'));
        const focusedItem = dropdownOptions[focusedIndex];
        if (focusedItem) {
            const optionValue = focusedItem.dataset.value; // Get the value of the option
            if (dropdownOptions.length > 1) {
                if (focusedIndex + 1 < dropdownOptions.length) {
                    dropdownOptions[focusedIndex+1].classList.add('dropdown-item-hover');
                } else {
                    dropdownOptions[0].classList.add('dropdown-item-hover');
                }
            }
            return optionValue ? optionValue : null;   
        }
        return null;
    }catch(error) {
        getToastErrorMessage('getSelectedOptionOnEnterUtil', 'utlis.js', error);
    }
}
// Remove focus indicators
export function removeHintIndicatorsOnCloseUtil(dropdownOptions) {
    try {
        dropdownOptions.forEach(option => {
            if (option.classList.contains('dropdown-item-hover')) {
                option.classList.remove('dropdown-item-hover');
            }
        });
    }catch(error) {
        getToastErrorMessage('removeHintIndicatorsOnCloseUtil', 'utlis.js', error);
    }
}
// Navigate with keyboard through the object picker options
export function handleMenuNavigationWithHintUtil(listOptions, event) {
    try {
        // Get the currently hinted option  
        const currentIndex = listOptions.findIndex((item) => item.classList.contains('dropdown-item-hover'));
        
        // Start index at 0 (default)
        let newIndex = 0;

        if (currentIndex === -1) {
            // No focused elements --> set the focus to the first or last item
            newIndex = event.key === 'ArrowDown' ? 0 : listOptions.length - 1;
            // Highlight the option
            listOptions[newIndex].classList.add('dropdown-item-hover');

            // Do not scroll into view if currentIndex = 0  -->  this ensure user sees search result info (unselectable)
            if (newIndex !== 0) {
                listOptions[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' }); // Ensure visibility
            }

            return; // Stop execution to prevent unintended movement
        }

        if (event.key === 'ArrowDown') {
            newIndex = (currentIndex + 1) % listOptions.length;  // Determine the next item's index  ---> Wrap around if at the end

        } else if(event.key === 'ArrowUp') {
            newIndex = (currentIndex - 1 + listOptions.length) % listOptions.length; // Determine the previous item's index --> Wrap around if at the start
        }

        listOptions[currentIndex].classList.remove('dropdown-item-hover'); // Remove the focus indicator fron the previous option
        listOptions[newIndex].classList.add('dropdown-item-hover');       // Add the focus indicator to the next option
        listOptions[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' }); // Scroll the current item into view
    }catch(error) {
        getToastErrorMessage('handleMenuNavigationWithHintUtil', 'utlis.js', error);
    }
    
}
// ---------------------------------------------------------------- \\


// ----------------- Open/Close Combobox Helpers ----------------- \\
// Close the dropdown  ---> must set <-this.isFocused-> = false
export function closeDropdownUtil(dropdownTrigger, dropdown) {
    try {
        setTimeout(() => {     
            dropdownTrigger.classList.remove('slds-is-open'); 
            dropdown.setAttribute('aria-expanded', "false");
        }, 20);
    }catch(error) {
        getToastErrorMessage('closeDropdownUtil', 'utlis.js', error);
    }
}
// Open the dropdown  ---> must set <-this.isFocused-> = true
export function openDropdownUtil(dropdownTrigger, dropdown) {
    try {
        if (dropdownTrigger && dropdown) {
            dropdownTrigger.classList.add('slds-is-open');
            dropdown.setAttribute('aria-expanded', "true");   
        }
    }catch(error) {
        getToastErrorMessage('openDropdownUtil', 'utlis.js', error);
    }
}
// Return true if the dropdown is open
export function isDropdownOpenUtil(dropdownTrigger) {
    try {
        return dropdownTrigger.classList.contains('slds-is-open');
    }catch(error) {
        getToastErrorMessage('isDropdownOpenUtil', 'utlis.js', error);
    }
}
// Toggle dropdown
export function toggleDropdownUtil(dropdownTrigger, dropdown, type=null) {
    try {
        if (type) {
            type === 'close' ? closeDropdownUtil(dropdownTrigger, dropdown) : openDropdownUtil(dropdownTrigger, dropdown);
        }
        // Check if dropdownTrigger is closed or open
        if (dropdownTrigger && dropdown) {
            isDropdownOpenUtil(dropdownTrigger) ? closeDropdownUtil(dropdownTrigger, dropdown) : openDropdownUtil(dropdownTrigger, dropdown);
        }
    }catch(error) {
        getToastErrorMessage('toggleDropdownUtil', 'utlis.js', error);
    }   
}
// ---------------------------------------------------------------- \\


// Remove focus indicator from options when menu is hovered
export function handleMenuMouseOverUtil(comboboxListOptions) {
    try {
        // Navigate the items if the dropdown is open
        const focusedItem = comboboxListOptions.find(option => option.classList.contains('dropdown-item-hover'));
        focusedItem?.classList.remove('dropdown-item-hover');
    }catch(error) {
        getToastErrorMessage('handleMenuMouseOverUtil', 'utlis.js', error);
    }
}
// Clicks on the dropdown Trigger
export function handleComboboxTriggerClickUtil(dropdownTrigger, dropdown) {
    try {
        if (!dropdownTrigger.classList.contains('slds-is-open'))  {
            openDropdownUtil(dropdownTrigger, dropdown);
        }
    }catch(error) {
        getToastErrorMessage('handleComboboxTriggerClickUtil', 'utlis.js', error);
    }
}
// Handle Enter, Escape, downArrow, upArrow  
/* 
    ***NEW: returns the selected record (must account for this)***
        const actionsObj = handleDropdownTriggerKeyDown(...params)
        if (actionsObj) {
            if (selectedOption) {
                this.selectedRecord = actionsObj.selectedRecord
            }
            if (actionsObj.isFocused) { this.isFocused = true; }
        }

*/
export function handleDropdownTriggerKeyDownUtil(event, dropdown, dropdownTrigger, listOptions, recordsArr) {
    try {
        const reactiveKeys = ['Escape', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
        const openTriggerKeys = ['Enter', 'ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
        const navigatingKeys = ['ArrowUp', 'ArrowDown'];
        const closeKeys = ['Tab', 'Escape'];

        
        // Get the dropdown & dropdown trigger
        const isDropdownOpenE1 = isDropdownOpenUtil(dropdownTrigger);

        if (/^[a-z0-9]$/i.test(event.key)) {
            if (!isDropdownOpenE1) {
                openDropdownUtil(dropdownTrigger, dropdown); // isClose=false --> open dropdown
            }
            return null;
        }

        // Return the tracked variables' new values in an object
        let actionsObj = {selectedOption: null, isFocused: false};
        
        if (reactiveKeys.some(key => event.key === key)) {
            event.preventDefault(); 

            if (isDropdownOpenE1 && event.key === 'Enter') {
                // Menu opened and 'Enter' clicked
                const optionValue = getSelectedOptionOnEnterUtil(listOptions);  // Get the selected item
                const selectedOption = optionValue ? recordsArr.find(opt => opt.value === optionValue) : null;
                closeDropdownUtil(dropdownTrigger, dropdown);
                actionsObj.selectedOption = selectedOption;

            } else if (!isDropdownOpenE1 && openTriggerKeys.some(key => event.key === key)) {
                // Open dropdown w/ focus
                actionsObj.isFocused = true;
                openDropdownUtil(dropdownTrigger, dropdown);   

                // If up or down arrow move faux-focus to a selection
                if (navigatingKeys.some(key => event.key === key)) {
                    handleMenuNavigationWithHintUtil(listOptions, event);  // Navigate the dropdown
                }


            } else if (isDropdownOpenE1 && closeKeys.some(close => event.key === close)) {
                closeDropdownUtil(dropdownTrigger, dropdown); // Close the dropdown
                removeHintIndicatorsOnCloseUtil(listOptions);

            } else if (isDropdownOpenE1 && navigatingKeys.some(key => event.key === key)) {
                handleMenuNavigationWithHintUtil(listOptions, event);  // Navigate the dropdown
            }

            return actionsObj;
        }
        return null;
    }catch(error) {
        getToastErrorMessage('handleDropdownTriggerKeyDownUtil', 'utlis.js', error);
    }
}
/* 
    ***NEW: returns the selected record (must account for this)***
        this.selectedRecord = handleComboboxOptionClickUtil(...params)
*/
export function handleComboboxOptionClickUtil(event, dropdownTrigger, dropdown, recordsArr) {
    try {
        event.stopPropagation();
        const val = event.currentTarget?.dataset?.value;
        if (val) {
            setTimeout(() => {
                try {
                    const selectedRecord = recordsArr.find(opt => opt.value === val);
                    closeDropdownUtil(dropdownTrigger, dropdown);
                    return selectedRecord;
                } catch (err) {
                    getToastErrorMessage('handleComboboxOptionClickUtil - setTimeout', 'utlis.js', err);
                }
            }, 0);
            
        }
    } catch (error) {
        getToastErrorMessage('handleComboboxOptionClickUtil', 'utlis.js', error);
    }
}
// ________________________________________________________________________________________________ \\ 
// ________________________________________________________________________________________________ \\ 
